import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card";
import { Icon } from "@iconify-icon/solid";
import { LoadingButton } from "../../components/ui/Button";
import { SelectInput } from "../../components/ui/form/SelectInput";
import useCheckupSession from "../../services/checkup/useCheckupSession";
import { onMount, For, Show, createEffect, createSignal } from "solid-js";
import { Button } from "@/components/shadcn/button";

export default function CheckupSession(props) {

  const { sessions, sessionForm, onSaveSession, handleSessionInput, onFetchSessions, addSession, removeSession } = useCheckupSession(props);
  const [expandedSessions, setExpandedSessions] = createSignal(new Set());

  onMount(() => {
    onFetchSessions();
    // Register save function with parent component
    if (props.onRegisterSave) {
      props.onRegisterSave(onSaveSession);
    }
  });
  
  const toggleSessionExpansion = (sessionKey) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionKey)) {
        newSet.delete(sessionKey);
      } else {
        newSet.add(sessionKey);
      }
      return newSet;
    });
  };

  // Track form changes - compare with original sessions
  createEffect(() => {
    if (props.onFormChange) {
      const originalSessions = props.sessions || [];
      const currentSessions = sessionForm.sessions || [];
      
      // Compare sessions to see if there are changes
      const hasChanges = JSON.stringify(originalSessions) !== JSON.stringify(currentSessions);
      props.onFormChange(hasChanges);
    }
  });

  const content = (
    <>
      <Show when={!props.hideCard}>
        <CardHeader>
          <CardTitle class="flex items-center gap-2">
            <Icon icon="mdi:stethoscope" class="text-gray-500 me-2" />
            Sessions
          </CardTitle>
        </CardHeader>
      </Show>
      <div class={props.hideCard ? "space-y-4" : ""}>
        {/* Add New Session */}
        <div class="pt-0">
          <div class="space-y-3">
            
            

            <div class="flex gap-2 mb-4">
            <div class="flex-1">
              <SelectInput
              label="Select Session"
              name="sessionId"
              form={sessionForm}
              options={sessions()}
              input={handleSessionInput}
              placeholder="Choose a session..."
            />
            </div>
            <div class="flex items-end">
              <Button variant="outline" onClick={addSession}>
                Add Session
              </Button>
            </div>
          </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mt-4">
              <For each={sessionForm.sessions}>
                {(session) => (
                  <div class="bg-gray-50 rounded-lg border dark:bg-gray-800 dark:border-gray-700">
                    {/* Session Header */}
                    <div class="flex items-center justify-between px-3 py-2">
                      <div class="flex-1">
                        <p class="font-medium text-sm">{session.name}</p>
                      </div>
                      <div class="flex items-center gap-2">
                        {/* Delete button */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeSession(session.key)}
                          class="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 w-8 h-8"
                        >
                          <Icon icon="material-symbols-light:delete-outline-rounded" class="w-4 h-4" />
                        </Button>
                        
                        {/* Collapsible button */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => toggleSessionExpansion(session.key)}
                          class="p-1 w-8 h-8"
                        >
                          <Icon 
                            icon={expandedSessions().has(session.key) ? "mdi:chevron-up" : "mdi:chevron-down"} 
                            class="w-4 h-4" 
                          />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Session Instructions - Collapsible */}
                    <Show when={expandedSessions().has(session.key)}>
                      <div class="px-3 pb-2 border-t border-gray-200 dark:border-gray-600">
                        <div class="pt-2 space-y-2">
                          <Show when={session.objectives}>
                            <div>
                              <h4 class="text-xs font-medium text-gray-700 dark:text-gray-300">Objectives:</h4>
                              <p class="text-sm text-gray-600 dark:text-gray-400">{session.objectives}</p>
                            </div>
                          </Show>
                          <Show when={session.instructions}>
                            <div>
                              <h4 class="text-xs font-medium text-gray-700 dark:text-gray-300">Instructions:</h4>
                              <p class="text-sm text-gray-600 dark:text-gray-400">{session.instructions}</p>
                            </div>
                          </Show>
                          <Show when={session.urduInstructions}>
                            <div>
                              <h4 class="text-xs font-medium text-gray-700 dark:text-gray-300">Urdu Instructions:</h4>
                              <p class="text-sm text-gray-600 dark:text-gray-400 font-urdu">{session.urduInstructions}</p>
                            </div>
                          </Show>
                        </div>
                      </div>
                    </Show>
                  </div>
                )}
              </For>
            </div>

            <Show when={!props.hideSaveButton}>
              <LoadingButton
                loading={props.loading}
                onClick={onSaveSession}
                disabled={!sessionForm.sessions}
              >
                Save Sessions
              </LoadingButton>
            </Show>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <Show when={props.hideCard} fallback={<Card><CardContent class="space-y-4">{content}</CardContent></Card>}>
      {content}
    </Show>
  );
}
